import axios from 'axios';
import { watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import ChatService from '@/Services/ChatService';
import PhaseService from '@/Services/PhaseService';
import useSessionStore from '@/Stores/useSessionStore';
import handleAsyncRequest from '@/Utils/handleAsyncRequest';
import handleLateNavigation from '@/Utils/handleLateNavigation';
import useChatMessagesStore from '@/Stores/useChatMessagesStore';
import SessionAnalyticsService from '@/Services/SessionAnalyticsService';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import { SmartGlassesClient } from '@/Services/SocketioClient';
import ConsultationAttendeesService from '@/Services/ConsultationAttendeesService';

import {
  COMMANDS,
  VICE_MESSAGE_TYPE,
  VICE_SESSION_PHASES,
  PHASE_STATUS,
  STETHOSCOPE_EVENTS,
  DISPLAY_NAME,
  SESSION_CALL_HELPERS,
  CAMERA_TYPES,
} from '@/Utils/vice-constants';

const sessionStore = useSessionStore();
const chatMessagesStore = useChatMessagesStore();
const multiStreamStore = useLocalStreamingDevicesStore();
const streamingDeviceStore = useIOTDevicesStore();

/**
 * Session Event and Commands Handlers for the User Session Call.
 * @returns
 */
function useSessionHandlers() {
  const onReceiveViceMessage = ({ attributes }) => {
    // Jitsi's sendCommandOnce echoes a sent command back to its own sender via MUC presence -
    // addChatMessage ignores that echo of our own message (matched by id) to avoid a duplicate.
    chatMessagesStore.addChatMessage({
      ...attributes,
      sender: JSON.parse(attributes.sender),
      phase: JSON.parse(attributes.phase),
      attachment:
        attributes.type === VICE_MESSAGE_TYPE.ATTACHMENT ? JSON.parse(attributes.attachment) : null,
    });
  };

  const onGoToNextPhase = async ({ attributes }) => {
    if (attributes.isVideoConference === 'true' && attributes.isConsultationVideo === 'true') {
      sessionStore.isConsultationCallStarted = true;
      sessionStore.togglePausedState(false);
    } else if (
      // eslint-disable-next-line operator-linebreak
      attributes.isVideoConference === 'true' &&
      attributes.isConsultationVideo === 'false'
    ) {
      sessionStore.isGroupCallStarted = true;
      sessionStore.togglePausedState(false);
    } else {
      sessionStore.isConsultationCallStarted = false;
      sessionStore.isGroupCallStarted = false;
    }
    if (Number(attributes.currentPhase) === VICE_SESSION_PHASES.SESSION_END) {
      sessionStore.isSessionEnded = true;
    } else {
      sessionStore.currentPhase = Number(attributes.currentPhase);
    }

    if (attributes.phaseEnded === 'true') {
      if (attributes.backToPreviousPhase === 'false') {
        sessionStore.phases[Number(attributes.currentPhase) - 1].status = PHASE_STATUS.COMPLETED;
      } else {
        sessionStore.phases[Number(attributes.currentPhase) + 1].status = PHASE_STATUS.COMPLETED;
      }
    } else {
      sessionStore.phases[attributes.currentPhase].status = PHASE_STATUS.LIVE;
    }

    // Forced session end
    if (attributes.isForcedEnd === 'true') {
      sessionStore.isSessionEnded = true;
      sessionStore.currentPhaseData.status = PHASE_STATUS.WAITING;
      sessionStore.phases[3].status = PHASE_STATUS.WAITING;
    }

    // set phases statuses and durations to initial values depends on phase from which we are switching
    // to previous step; change phases wizar
    if (attributes.backToPreviousPhase === 'true') {
      console.log('BACK TO PREVIUS PHASE!');

      streamingDeviceStore.devices.smartglasses.disconnecting = false;

      // delete all external cameras from the store
      // multiStreamStore.clearExternalCamerasList();

      switch (true) {
        // case when we are moving back from 'Consultation' pahse to 'Introduction' phase
        case attributes.currentPhase === String(VICE_SESSION_PHASES.INTRODUCTION) &&
          attributes.phaseEnded === 'true':
          sessionStore.setPhaseStatusToWaiting(VICE_SESSION_PHASES.INTRODUCTION);
          sessionStore.setPhaseDurationToZero(VICE_SESSION_PHASES.INTRODUCTION);

          sessionStore.setPhaseStatusToWaiting(VICE_SESSION_PHASES.CONSULTATION);
          sessionStore.setPhaseDurationToZero(VICE_SESSION_PHASES.CONSULTATION);

          break;
        // case when we are moving back from 'Debrief' pahse to 'Consultation' phase
        case attributes.currentPhase === String(VICE_SESSION_PHASES.CONSULTATION) &&
          attributes.phaseEnded === 'true':
          sessionStore.setPhaseStatusToWaiting(VICE_SESSION_PHASES.CONSULTATION);
          sessionStore.setPhaseDurationToZero(VICE_SESSION_PHASES.CONSULTATION);

          sessionStore.setPhaseStatusToWaiting(VICE_SESSION_PHASES.DEBRIEF);
          sessionStore.setPhaseDurationToZero(VICE_SESSION_PHASES.DEBRIEF);

          break;
        default:
      }
    } else {
      console.log('GO TO THE NEXT PHASE!');
    }
  };

  const onPauseCurrentPhase = ({ attributes }) => {
    if (attributes.isPhasePaused === 'true') {
      sessionStore.togglePausedState(true);
    } else {
      sessionStore.togglePausedState(false);
    }
  };

  const onMessageDismissed = async ({ attributes }) => {
    if (attributes.id && attributes.is_read === 'true') {
      // eslint-disable-next-line no-unused-vars
      const [_res, err] = await handleAsyncRequest(
        ChatService.handleMessage(attributes.id, sessionStore.sessionId),
      );
      if (!err) {
        const dissmissedMessage = chatMessagesStore.allChatMessages.find(
          (msg) => attributes.id == msg.id,
        );
        if (dissmissedMessage) {
          dissmissedMessage.handledAt = attributes.handle_at;
        }
      }
    }
  };

  // Helper Functions
  const onAttendeeEndPhase = (endingPhase) => {
    sessionStore.isGroupCallStarted = false;
    sessionStore.phases[endingPhase.id].status = PHASE_STATUS.COMPLETED;
    if (endingPhase.id < VICE_SESSION_PHASES.DEBRIEF) {
      sessionStore.currentPhase = endingPhase.id + 1;
    } else if (endingPhase.id === VICE_SESSION_PHASES.DEBRIEF) {
      sessionStore.isSessionEnded = true;
    }
  };

  const onClinicianEndPhase = async (endingPhase, isForcedEnd, sendJitsiCommand) => {
    sessionStore.isGroupCallStarted = false;
    sessionStore.phases[endingPhase.id].status = PHASE_STATUS.COMPLETED;
    sessionStore.togglePausedState(false);

    // If the phase being ended is CONSULTATION,
    // send a consultation_ended message to the smart glass
    if (endingPhase.id === VICE_SESSION_PHASES.CONSULTATION) {
      // sessionStore.deviceMessageSender.turnOffSmartGlass();
      // send disconnect event to the server to socket io connection and this prevents
      // disconnects the stethoscope stream
      sessionStore.iotSocket?.emit(STETHOSCOPE_EVENTS.DEVICE_DISCONNECTED);
      multiStreamStore.disableMultiCameraStream();
    }

    // Always check availability of screenshare user and kick the
    // user out when going to a different phase
    sendJitsiCommand.kickScreensharingUser();

    if (endingPhase.id === VICE_SESSION_PHASES.INTRODUCTION) {
      updateConsultationParticipantRestrictions();
    }

    if (endingPhase.id < VICE_SESSION_PHASES.DEBRIEF) {
      sessionStore.currentPhase = endingPhase.id + 1;
    } else if (endingPhase.id === VICE_SESSION_PHASES.DEBRIEF) {
      sessionStore.isSessionEnded = true;
    }

    sendJitsiCommand.goToNextPhase(
      Number(endingPhase.id) + 1,
      sessionStore.isGroupCallStarted,
      false,
      true,
      isForcedEnd,
      false,
    );

    // send system message about phase end
    chatMessagesStore.sendChatMessage({
      id: uuidv4(),
      content: isForcedEnd
        ? 'This session has ended'
        : `${sessionStore.phases[endingPhase.id].title} has ended`,
      type: 'system',
      sendTo: 'participants',
    });

    // Set the current phase in the cache
    await PhaseService.endPhase({
      // title: sessionStore.phases[endingPhase.id].title,
    });

    // Update Session Analytics when phase ends
    await SessionAnalyticsService.endPhase({
      id: `${sessionStore.sessionDBId}`,
      type: sessionStore.phases[endingPhase.id].title,
    });

    // Forced end
    if (isForcedEnd) {
      axios.post(route('finish.session', sessionStore.sessionUniqueId));
      window.location.href = route('dashboard.clinician');
    }
  };

  const onClinicianBackToPreviousPhase = async (endingPhase, isForcedEnd, sendJitsiCommand) => {
    sessionStore.isGroupCallStarted = false;

    // sessionStore.phases[endingPhase.id].status = PHASE_STATUS.COMPLETED;
    sessionStore.togglePausedState(false);

    // If the phase being ended is CONSULTATION,
    // send a consultation_ended message to the smart glass
    if (endingPhase.id === VICE_SESSION_PHASES.CONSULTATION) {
      // sessionStore.deviceMessageSender.turnOffSmartGlass();
      // send disconnect event to the server to socket io connection and this prevents
      // disconnects the stethoscope stream
      sessionStore.iotSocket?.emit(STETHOSCOPE_EVENTS.DEVICE_DISCONNECTED);
      multiStreamStore.disableMultiCameraStream();
    }

    // Always check availability of screenshare user and kick the
    // user out when going to a different phase
    sendJitsiCommand.kickScreensharingUser();

    if (endingPhase.id === VICE_SESSION_PHASES.INTRODUCTION) {
      updateConsultationParticipantRestrictions();
    }

    sessionStore.currentPhase = endingPhase.id - 1;

    sendJitsiCommand.goToNextPhase(
      Number(endingPhase.id) - 1,
      sessionStore.isGroupCallStarted,
      false,
      true,
      isForcedEnd,
      true,
    );

    // send system message about phase end
    chatMessagesStore.sendChatMessage({
      id: uuidv4(),
      content: isForcedEnd
        ? 'This session has ended'
        : `${sessionStore.phases[endingPhase.id].title} has ended`,
      type: 'system',
      sendTo: 'participants',
    });

    // await PhaseService.update({
    //   id: `${sessionStore.sessionUniqueId}`,
    //   title: sessionStore.phases[endingPhase.id].title,
    //   status: PHASE_STATUS.ENDED,
    //   timestamp: Date.now(),
    // });

    // Set the current phase in the cache
    await PhaseService.endPhase({
      // title: sessionStore.phases[endingPhase.id].title,
      backToPreviousPhase: true,
    });

    // Update Session Analytics when phase ends
    await SessionAnalyticsService.endPhase({
      id: `${sessionStore.sessionDBId}`,
      type: sessionStore.phases[endingPhase.id].title,
    });

    // if (endingPhase.id == VICE_SESSION_PHASES.DEBRIEF) {
    //  axios.post(route('finish.session', sessionStore.sessionUniqueId));
    // }
  };

  const updateConsultationParticipantRestrictions = () => {
    const participantIdsAllowedInConsultation = Object.keys(
      sessionStore.activeCallParticipants[VICE_SESSION_PHASES.INTRODUCTION],
    );
    ConsultationAttendeesService.setAllowedUserIds(
      sessionStore.sessionUniqueId,
      participantIdsAllowedInConsultation,
    );
  };

  // Set next phase by the Clinician
  const onClinicianSetNextPhase = async (activePhase, sendJitsiCommand) => {
    let isConsultationVideo = false;
    sessionStore.phases[activePhase.id].status = PHASE_STATUS.LIVE;
    if (Number(activePhase.id) + 1 === 3) {
      isConsultationVideo = true;
      sessionStore.isConsultationCallStarted = true;
    } else {
      sessionStore.isGroupCallStarted = true;
    }

    sendJitsiCommand.goToNextPhase(activePhase.id, true, isConsultationVideo, false, false, false);

    // send system message about phase start
    chatMessagesStore.sendChatMessage({
      id: uuidv4(),
      content: `${sessionStore.phases[activePhase.id].title} started`,
      type: 'system',
      sendTo: 'participants',
    });

    // Set the current phase in the cache
    await PhaseService.startPhase({
      title: sessionStore.phases[activePhase.id].title,
    });

    // TODO: Remove old api
    // Update session phase
    // await PhaseService.update({
    //   id: `${sessionStore.sessionUniqueId}`,
    //   title: sessionStore.phases[activePhase.id].title,
    //   status: PHASE_STATUS.STARTED,
    //   timestamp: Date.now(),
    // });

    // Update Session Analytics when phase starts
    await SessionAnalyticsService.startPhase({
      id: `${sessionStore.sessionDBId}`,
      type: sessionStore.phases[activePhase.id].title,
    });
  };

  const initializeChatMessages = async () => {
    const [messageRes, messageErr] = await handleAsyncRequest(
      ChatService.fetchMessageThread(sessionStore.sessionUniqueId),
    );
    if (!messageErr) {
      chatMessagesStore.chatMessages = messageRes.data;
    }
  };

  const onHandleNavigation = async (isParticipant) => {
    const [cachedPhaseData, cachedPhaseErr] = await handleAsyncRequest(
      PhaseService.getTimes(`${sessionStore.sessionUniqueId}`),
    );

    if (!cachedPhaseErr && cachedPhaseData) {
      handleLateNavigation({
        isParticipant,
        cachedPhaseData,
      });
    }
    sessionStore.phasesDataInitialized = true;
  };

  const onUpdateSessionAnalytics = async () => {
    await SessionAnalyticsService.fetchSinglePhase({
      id: `${sessionStore.sessionDBId}`,
      phaseTitle: sessionStore.currentPhaseData.title,
    });
    await SessionAnalyticsService.fetchAllPhases(`${sessionStore.sessionDBId}`);
  };

  const onResetSession = async () => {
    await PhaseService.resetPhasesData().then(() => {
      window.location.reload();
    });
  };

  // Watchers specific to the SessionCall component.
  const handleResetMyAudio = (shouldTurnMicOn) => {
    multiStreamStore.toggleMicrophone(CAMERA_TYPES.INTERNAL_WEBCAM, !shouldTurnMicOn);
  };
  const handleResetMyVideo = (shouldTurnCamOn) => {
    multiStreamStore.toggleCamera(CAMERA_TYPES.INTERNAL_WEBCAM, shouldTurnCamOn);
  };
  watch(
    () => sessionStore.isPhasePaused,
    async (isPaused) => {
      if (sessionStore.isClinician) {
        chatMessagesStore.sendChatMessage({
          id: uuidv4(),
          content: `${sessionStore.currentPhaseData.title} ${isPaused ? 'was paused' : 'resumed'}`,
          type: 'system',
          sendTo: 'participants',
        });

        // await PhaseService.update({
        //   id: `${sessionStore.sessionUniqueId}`,
        //   title: sessionStore.currentPhaseData.title,
        //   status: isPaused ? PHASE_STATUS.PAUSED : PHASE_STATUS.RESUMED,
        //   timestamp: Date.now(),
        // });

        if (isPaused) {
          await PhaseService.pausePhase({
            title: sessionStore.currentPhaseData.title,
          });
        } else {
          await PhaseService.resumePhase({
            title: sessionStore.currentPhaseData.title,
          });
        }
        // Toggle audio and video mute states for Clinician onPause
        if (isPaused) {
          handleResetMyAudio(false);
          handleResetMyVideo(false);
        } else {
          handleResetMyAudio(true);
          handleResetMyVideo(true);
        }
      }
    },
  );

  watch(
    () => sessionStore.isPutOnHold,
    (isPutOnHold) => {
      if (sessionStore.isClinician) return;
      if (isPutOnHold) {
        handleResetMyAudio(false);
        handleResetMyVideo(false);
      } else {
        handleResetMyVideo(true);
      }
    },
  );

  // Mute microphone when switching to Vuzix
  watch(
    () => multiStreamStore.externalAudioIO,
    (isExternalAudioIO) => {
      handleResetMyAudio(!isExternalAudioIO);
    },
  );

  const onTrackAdded = (track) => {
    if (!track.isLocal()) {
      // Get displayName from the jitsid and push the tracks to the store
      const displayName = Object.keys(multiStreamStore.externalDevicePreview).find(
        (previewDeviceName) =>
          multiStreamStore.externalDevicePreview[previewDeviceName].jitsiId ===
          track.getParticipantId(),
      );
      if (displayName) multiStreamStore.externalDevicePreview[displayName].tracks.push(track);
    }
  };

  const previewDevicesList = [
    DISPLAY_NAME.VUZIX,
    DISPLAY_NAME.SCREENSHARE_USER,
    DISPLAY_NAME.STETHOSCOPE,
  ];

  const onUserJoined = async (id, user) => {
    const displayName = user.getDisplayName();
    if (!previewDevicesList.includes(displayName)) return;
    multiStreamStore.updateExternalDevicePreview(displayName, 'jitsiId', id);
    streamingDeviceStore.iotSockets.smartglasses = await SmartGlassesClient(sessionStore.sessionId);
    if (displayName === DISPLAY_NAME.VUZIX) {
      multiStreamStore.externalDevicePreview[displayName].isActive = true;
    }
  };

  const events = {
    CONFERENCE: {
      TRACK_ADDED: onTrackAdded,
      USER_JOINED: onUserJoined,
    },
    CONN_QUALITY: {},
  };

  const commands = {
    [COMMANDS.GO_TO_NEXT_PHASE]: onGoToNextPhase,
    [COMMANDS.SEND_VICE_MESSAGE]: onReceiveViceMessage,
    [COMMANDS.PAUSE_CURRENT_PHASE]: onPauseCurrentPhase,
    [COMMANDS.MESSAGE_DISSMISED_COMMAND]: onMessageDismissed,
  };

  const helpers = {
    [SESSION_CALL_HELPERS.RESET_SESSION]: onResetSession,
    [SESSION_CALL_HELPERS.HANDLE_NAVIGATION]: onHandleNavigation,
    [SESSION_CALL_HELPERS.ATTENDEE_END_PHASE]: onAttendeeEndPhase,
    [SESSION_CALL_HELPERS.CLINICIAN_END_PHASE]: onClinicianEndPhase,
    [SESSION_CALL_HELPERS.INITIALIZE_CHAT_MESSAGES]: initializeChatMessages,
    [SESSION_CALL_HELPERS.CLINICIAN_SET_NEXT_PHASE]: onClinicianSetNextPhase,
    [SESSION_CALL_HELPERS.UPDATE_SESSION_ANALYTICS]: onUpdateSessionAnalytics,
    [SESSION_CALL_HELPERS.CLINICIAN_BACK_TO_PREVIOUS_PHASE]: onClinicianBackToPreviousPhase,
  };

  return {
    events,
    commands,
    helpers,
  };
}

export default useSessionHandlers;
